export interface User {
	id: number
	name: string
	username: string
	email: string | null
	type: string
	access_token: string | null
	refresh_token: string | null
	trial_expires_at: string | null
	subscription_started_at: string | null
	subscription_expires_at: string | null
	subscription_store: string | null
	subscription_refunded_at: string | null
	subscription_plan_identifier: string | null
	subscription_management_url: string | null
	unsubscribed_at: string | null
}

export interface SendingChatEntry {
	text: string
	images: { data: string; name: string }[]
}

export interface LibraryVocabulary {
	id: number
	word: string
	translation: string | null
	is_ai_recommended: boolean
	learning_stage: number
	created_at: string
	practiced_count: number
	last_practiced_at: string | null
	client_id: any
	collections: LibraryCollection[]
}

export interface LibraryGrammar {
	id: number
	grammar_topic: string
	client_id: any
}

export interface Chat {
	id: number
	user_id: number
	title: string
	focus: string
	focus_minor: string[]
	created_at: string
	entries: ChatEntry[]
}

export interface ChatEntry {
	id: number | undefined
	ai_chat_learning_id: number | undefined
	client_id: string
	user_id: number
	message: MessageChatEntry
	learning: LearningChatEntry
	is_loading: boolean
}

export interface BaseChatEntry {
	id: number
	created_at: string
}

export interface MessageChatEntry extends BaseChatEntry {
	text: string
	attachments: {
		images: string[]
	}
}

export interface LearningChatEntry extends BaseChatEntry {
	related_topics: string[]
	vocabulary_knowledge: {
		new: LibraryVocabulary[]
		existing: LibraryVocabulary[]
	}
	grammar_knowledge: {
		new: LibraryGrammar[]
		existing: LibraryGrammar[]
	}
}

export interface LearningLanguage {
	name: string
	name_en: string
	icon: string
	dir?: string
	dayjs_locale?: any
}

export interface LibraryOverview {
	availableLearningLanguages: Record<string, LearningLanguage>
	vocabulary: LibraryVocabulary[]
	hasMoreVocabulary: boolean
}

enum PracticeSetStatus {
	PENDING = 'pending',
	READY = 'ready',
	PRACTICED = 'practiced',
	FAILED = 'failed',
}

export interface PracticeSetAnswer {
	result: any
	result_format: string
	correct_count: number
	items_count: number
	practiced_at: string
}

export enum PracticeVocabularyLearningProgressType {
	JUST_LEARNED = 'just_learned',
	IMPROVED_LEARNING = 'improved_learning',
}

export interface AiQueryResponse {
	chat: Chat
	response_entries: ChatEntry[]
}

export interface PracticeSet {
	id: number
	user_id: number
	status: PracticeSetStatus
	items_count: number
	correct_answer_count: number
	type: string
	created_at: string
	practiced_at: string | null
	user_limited_id: number
}

export interface PracticeSetItem {
	id: number
}

export interface PracticeSetItemVocabulary extends PracticeSetItem {
	vocabulary: LibraryVocabulary
}

export interface PracticeSetItemSentence extends PracticeSetItem {
	sentence: string
	translated_sentence: string
	vocabularies: LibraryVocabulary[]
}

export interface PracticeSetItemChoice extends PracticeSetItem {
	vocabulary: LibraryVocabulary
	other_vocabulary_choices: string[]
}

export interface PracticeOverview {
	vocabulary: {
		recent_practice_sets: PracticeSet[]
		daily_practice: {
			date: string
			short_date: string
			correct_count: number
			incorrect_count: number
		}[]
		stats: {
			needs_work_count: number
			known_count: number
			learned_count: number
		}
	}
}

export interface LibraryCollection {
	id: number
	name: string
	color: string
	created_at: string
	updated_at: string
	vocabularies: LibraryVocabulary[]
	grammars: LibraryGrammar[]
	chats: Chat[]
	vocabularies_count: number
	grammars_count: number
	chats_count: number
}
